import React, { ChangeEvent, FormEvent, useEffect, useRef, useState } from 'react';
import {
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  User,
} from 'firebase/auth';
import './App.css';
import {
  ensureFirebaseAuth,
  getFirebaseAuth,
  isFirebaseConfigured,
  missingFirebaseConfigKeys,
  shouldSendFirebaseIdToken,
} from './lib/firebase';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const PASSWORD_RESET_SUCCESS_MESSAGE =
  '入力されたメールアドレス宛にパスワード再設定メールを送信しました。届かない場合は入力内容を確認してください。';

type AuthView = 'login' | 'passwordReset';

const AUTH_ERROR_MESSAGES: Record<string, string> = {
  'auth/invalid-email': 'メールアドレスの形式が正しくありません。',
  'auth/invalid-credential': 'メールアドレスまたはパスワードが正しくありません。',
  'auth/missing-password': 'パスワードを入力してください。',
  'auth/network-request-failed': 'ネットワークエラーが発生しました。接続を確認してください。',
  'auth/too-many-requests': '試行回数が多すぎます。少し時間を置いてから再度お試しください。',
  'auth/user-disabled': 'このアカウントは無効化されています。',
  'auth/user-not-found': '該当するユーザーが見つかりません。',
  'auth/wrong-password': 'メールアドレスまたはパスワードが正しくありません。',
};

const getFirebaseErrorCode = (error: unknown): string | null =>
  typeof error === 'object' &&
  error !== null &&
  'code' in error &&
  typeof error.code === 'string'
    ? error.code
    : null;

const getAuthErrorMessage = (error: unknown): string => {
  const errorCode = getFirebaseErrorCode(error);

  if (errorCode && AUTH_ERROR_MESSAGES[errorCode]) {
    return AUTH_ERROR_MESSAGES[errorCode];
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return '認証に失敗しました。時間を置いてから再度お試しください。';
};

function App() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState<boolean>(isFirebaseConfigured);
  const [isAuthenticating, setIsAuthenticating] = useState<boolean>(false);
  const [authView, setAuthView] = useState<AuthView>('login');
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [authError, setAuthError] = useState<string | null>(null);
  const [authSuccessMessage, setAuthSuccessMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const auth = getFirebaseAuth();

    if (!auth) {
      setIsAuthLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setIsAuthLoading(false);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (currentUser) {
      return;
    }

    setSelectedFile(null);
    setSuccessMessage(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [currentUser]);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setSelectedFile(event.target.files[0]);
      setError(null);
      setSuccessMessage(null);
    } else {
      setSelectedFile(null);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setSuccessMessage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const showAuthView = (view: AuthView) => {
    setAuthView(view);
    setAuthError(null);
    setAuthSuccessMessage(null);

    if (view === 'passwordReset') {
      setPassword('');
    }
  };

  const handleAuthSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAuthError(null);
    setAuthSuccessMessage(null);

    if (!email.trim()) {
      setAuthError('メールアドレスを入力してください。');
      return;
    }

    if (!password) {
      setAuthError('パスワードを入力してください。');
      return;
    }

    setIsAuthenticating(true);

    try {
      const auth = await ensureFirebaseAuth();
      await signInWithEmailAndPassword(auth, email.trim(), password);
      setPassword('');
    } catch (err) {
      setAuthError(getAuthErrorMessage(err));
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handlePasswordResetSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAuthError(null);
    setAuthSuccessMessage(null);

    if (!email.trim()) {
      setAuthError('メールアドレスを入力してください。');
      return;
    }

    setIsAuthenticating(true);

    try {
      const auth = await ensureFirebaseAuth();
      await sendPasswordResetEmail(auth, email.trim());
      setAuthSuccessMessage(PASSWORD_RESET_SUCCESS_MESSAGE);
    } catch (err) {
      const errorCode = getFirebaseErrorCode(err);

      if (errorCode === 'auth/user-not-found') {
        setAuthSuccessMessage(PASSWORD_RESET_SUCCESS_MESSAGE);
      } else {
        setAuthError(getAuthErrorMessage(err));
      }
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleSignOut = async () => {
    setAuthError(null);
    setAuthSuccessMessage(null);

    try {
      const auth = await ensureFirebaseAuth();
      await signOut(auth);
      setAuthSuccessMessage('ログアウトしました。');
      setError(null);
      setAuthView('login');
    } catch (err) {
      setAuthError(getAuthErrorMessage(err));
    }
  };

  const handleUploadAndDownload = async () => {
    if (!currentUser) {
      setError('アップロードするにはログインしてください。');
      return;
    }

    if (!selectedFile) {
      setError('ファイルを選択してください。');
      return;
    }

    setIsLoading(true);
    setError(null);
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const headers: HeadersInit = {};

      if (shouldSendFirebaseIdToken) {
        const idToken = await currentUser.getIdToken();
        headers.Authorization = `Bearer ${idToken}`;
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/process-excel`, {
        method: 'POST',
        body: formData,
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.detail || `ファイルの処理に失敗しました。ステータス: ${response.status}`
        );
      }

      const contentDisposition = response.headers.get('content-disposition');
      let filename =
        selectedFile.name.replace(/(\.[^/.]+)+$/, '') +
        '_processed' +
        selectedFile.name.match(/(\.[^/.]+)+$/)?.[0];

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch && filenameMatch.length > 1) {
          filename = filenameMatch[1];
        }
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setSuccessMessage('ファイルの処理が完了し、ダウンロードが開始されました。');
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('不明なエラーが発生しました。');
      }
      console.error('Process and download error:', err);
      setSuccessMessage(null);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>照査報告書自動作成システム</h1>
      </header>
      <main className="App-main">
        <div className="content-shell">
          {!currentUser ? (
            <section className="auth-section">
              <div className="section-intro">
                <h2>{authView === 'passwordReset' ? 'パスワード再設定' : 'Firebaseでログイン'}</h2>
                <p className="section-description">
                  {authView === 'passwordReset'
                    ? '登録済みのメールアドレスを入力してください。'
                    : '招待済みのアカウントだけが利用できます。'}
                </p>
              </div>

              {!isFirebaseConfigured ? (
                <div className="config-panel">
                  <p className="config-text">
                    Firebase の公開設定が不足しています。以下の環境変数を `.env.local` と Vercel
                    に追加してください。
                  </p>
                  <ul className="config-list">
                    {missingFirebaseConfigKeys.map((key) => (
                      <li key={key}>{key}</li>
                    ))}
                  </ul>
                  <p className="auth-helper-text">
                    Firebase Console の Authentication &gt; Sign-in method で
                    Email/Password を有効にするとログインできます。
                  </p>
                </div>
              ) : isAuthLoading ? (
                <div className="status-panel">
                  <div className="spinner spinner-dark"></div>
                  <p>ログイン状態を確認しています...</p>
                </div>
              ) : authView === 'passwordReset' ? (
                <form className="auth-form" onSubmit={handlePasswordResetSubmit}>
                  <label className="form-field">
                    <span>メールアドレス</span>
                    <input
                      type="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      autoComplete="email"
                      placeholder="name@example.com"
                    />
                  </label>

                  {authError && <p className="error-message">{authError}</p>}
                  {authSuccessMessage && <p className="success-message">{authSuccessMessage}</p>}

                  <button type="submit" className="primary-button" disabled={isAuthenticating}>
                    {isAuthenticating ? '送信中...' : '再設定メールを送信する'}
                  </button>

                  <button
                    type="button"
                    className="text-button"
                    onClick={() => showAuthView('login')}
                  >
                    ログイン画面へ戻る
                  </button>
                </form>
              ) : (
                <form className="auth-form" onSubmit={handleAuthSubmit}>
                  <label className="form-field">
                    <span>メールアドレス</span>
                    <input
                      type="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      autoComplete="email"
                      placeholder="name@example.com"
                    />
                  </label>

                  <label className="form-field">
                    <span>パスワード</span>
                    <input
                      type="password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      autoComplete="current-password"
                      placeholder="初回パスワードまたは設定済みパスワード"
                    />
                  </label>

                  {authError && <p className="error-message">{authError}</p>}
                  {authSuccessMessage && <p className="success-message">{authSuccessMessage}</p>}

                  <button type="submit" className="primary-button" disabled={isAuthenticating}>
                    {isAuthenticating ? 'ログイン中...' : 'ログインする'}
                  </button>

                  <button
                    type="button"
                    className="text-button"
                    onClick={() => showAuthView('passwordReset')}
                  >
                    パスワードを再設定する
                  </button>
                </form>
              )}
            </section>
          ) : (
            <section className="upload-section">
              <div className="session-bar">
                <div>
                  <p className="signed-in-label">ログイン中</p>
                  <p className="signed-in-user">{currentUser.email || 'メールアドレス未取得'}</p>
                </div>
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="secondary-button compact-button"
                >
                  ログアウト
                </button>
              </div>

              <div className="upload-content">
                <div>
                  <h2>照査対象ファイルをアップロード</h2>
                  <p className="upload-description">
                    照査報告書作成の元となる Excel ファイル（.xlsx または .xls）を選択してください。
                  </p>
                </div>

                <input
                  type="file"
                  onChange={handleFileChange}
                  ref={fileInputRef}
                  accept=".xlsx,.xls"
                  id="file-upload"
                  className="file-input"
                />
                <label
                  htmlFor="file-upload"
                  className={`file-dropzone ${isLoading ? 'disabled' : ''}`}
                >
                  <span className="file-dropzone-badge" aria-hidden="true">
                    Excel
                  </span>
                  <span className="file-dropzone-title">
                    {selectedFile ? selectedFile.name : 'Excelファイルを選択'}
                  </span>
                  <span className="file-dropzone-description">
                    .xlsx または .xls に対応しています
                  </span>
                </label>
                {selectedFile && (
                  <div className="selected-file-row">
                    <div>
                      <p className="selected-file-label">選択中のファイル</p>
                      <p className="selected-file-name">{selectedFile.name}</p>
                    </div>
                    <button
                      onClick={handleRemoveFile}
                      className="secondary-button"
                      disabled={isLoading}
                      type="button"
                    >
                      ファイルを削除
                    </button>
                  </div>
                )}
              </div>

              {shouldSendFirebaseIdToken && (
                <p className="token-note">
                  Firebase ID トークンを API に送信する設定が有効です。バックエンド側で Bearer
                  トークン検証を行ってください。
                </p>
              )}

              {error && <p className="error-message">{error}</p>}
              {authError && <p className="error-message">{authError}</p>}
              {successMessage && <p className="success-message">{successMessage}</p>}

              <button
                onClick={handleUploadAndDownload}
                disabled={!selectedFile || isLoading}
                className="process-button"
                type="button"
              >
                {isLoading ? (
                  <>
                    <div className="spinner"></div>
                    <span className="loading-text">処理中です。しばらくお待ちください...</span>
                  </>
                ) : (
                  '報告書作成 ＆ ダウンロード'
                )}
              </button>
            </section>
          )}
        </div>
      </main>
      <footer className="App-footer">
        <p>&copy; {new Date().getFullYear()} EggAI</p>
      </footer>
    </div>
  );
}

export default App;
